import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FileText, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateSitemapIndex, getSitemapFile } from '../utils/sitemap';

export function SitemapPage() {
  const [sitemapIndex, setSitemapIndex] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    async function loadSitemapIndex() {
      try {
        const xml = await generateSitemapIndex();
        setSitemapIndex(xml);
        setError(null);
      } catch (error) {
        console.error('Error loading sitemap index:', error);
        setError('Failed to load sitemap index');
      } finally {
        setLoading(false);
      }
    }

    loadSitemapIndex();
  }, []);

  const handleFileSelect = async (filename: string) => {
    try {
      setSelectedFile(filename);
      setFileContent(null); // Clear previous content
      const content = await getSitemapFile(filename);
      setFileContent(content);
    } catch (error) {
      console.error('Error loading sitemap file:', error);
      toast.error('Failed to load sitemap file');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      setSelectedFile(null);
      setFileContent(null);
      const xml = await generateSitemapIndex();
      setSitemapIndex(xml);
      setError(null);
      toast.success('Sitemap index refreshed successfully');
    } catch (error) {
      console.error('Error refreshing sitemap index:', error);
      setError('Failed to refresh sitemap index');
      toast.error('Failed to refresh sitemap index');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600 flex items-center">
          <FileText className="w-6 h-6 mr-2 animate-pulse" />
          Loading sitemap...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-red-600">
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sitemap - Plumbers.Guru</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 min-h-screen">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <FileText className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Sitemap Index</h1>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh Sitemaps'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Sitemap Index</h2>
            <pre className="bg-gray-50 p-6 rounded-lg overflow-x-auto shadow-md font-mono text-sm">
              {sitemapIndex}
            </pre>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Sitemap Files</h2>
            {selectedFile ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-700">{selectedFile}</h3>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Back to List
                  </button>
                </div>
                <pre className="bg-gray-50 p-6 rounded-lg overflow-x-auto shadow-md font-mono text-sm">
                  {fileContent}
                </pre>
              </>
            ) : (
              <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                <div className="space-y-2">
                  {sitemapIndex.match(/sitemap-\d+\.xml/g)?.map((filename) => (
                    <button
                      key={filename}
                      onClick={() => handleFileSelect(filename)}
                      className="w-full text-left px-4 py-2 rounded hover:bg-gray-100 flex items-center"
                    >
                      <FileText className="w-5 h-5 text-blue-600 mr-2" />
                      {filename}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}